import { Client } from "@microsoft/microsoft-graph-client";
import {
  DataIntegrityReport,
  ExcelClinicInfo
} from "../types";
import {
  fetchRelevantWorksheets,
  SpecialtyClinicWorksheet
} from "./ExcelClinicsService";
import {
  getColumLetterWhichisExactly,
  parseWorksheetFormattedForClosures as parseWorksheetFormattedForClosuresIgnorePast,
  parseWorksheetFormattedForOpenings as parseWorksheetFormattedForOpeningsIgnorePast
} from "./ExcelWorkbookParsers";

export async function fetchAndParseExcelClinicData(
  graphClient: Client
): Promise<ExcelClinicInfo[]> {
  const wsheets = await fetchRelevantWorksheets(graphClient);
  let promisedParsingResults: Promise<ExcelClinicInfo>[] = [];
  wsheets.forEach((ws) => {
    promisedParsingResults.push(
      parseClinicInfoFromWorksheetFormatAgnostic(ws, graphClient)
    );
  });

  const organizedPromises = await onceAllSettledMergeResultsByClinic(
    promisedParsingResults
  );

  return organizedPromises;
}

//Interprets the format and parses accordingly, note, extra network request to determine which type
async function parseClinicInfoFromWorksheetFormatAgnostic(
  ws: SpecialtyClinicWorksheet,
  graphClient: Client
): Promise<ExcelClinicInfo> {
  try {
    if (
      (await getColumLetterWhichisExactly(ws, "CHAIRS LEFT", graphClient)) !==
      undefined
    ) {
      let res = await parseWorksheetFormattedForOpeningsIgnorePast(
        ws,
        true,
        graphClient
      );
      return Promise.resolve({
        clinicName: ws.clinicEnum,
        datesAndState: [],
        datesAndChairs: res.results,
        dataIntegrityReport: res.dataIntegrityReport,
      });
    } else {
      let res = await parseWorksheetFormattedForClosuresIgnorePast(
        ws,
        true,
        graphClient
      );
      return Promise.resolve({
        clinicName: ws.clinicEnum,
        datesAndState: res.results,
        datesAndChairs: [],
        dataIntegrityReport: res.dataIntegrityReport,
      });
    }
  } catch (e) {
    const parsingFailedDataIntegretyReport: DataIntegrityReport = {
      URL: ws.URI,
      accessedSuccessfully: false,
      errorSummary: "This clinic's excel data caused our parser to crash. Showing home availability.",
      possiblyHumanInterperableAmbiguities: []
    }

    const rejectionData: ExcelClinicInfo = {
      clinicName: ws.clinicEnum,
      datesAndState: [],
      datesAndChairs: [],
      dataIntegrityReport: parsingFailedDataIntegretyReport,
    }
    return Promise.reject(rejectionData);
    
  }
}

async function onceAllSettledMergeResultsByClinic(
  SpecialtyClinicInfoPromises: Promise<ExcelClinicInfo>[]
) {
  const organizedExcelClinicInfoArr: ExcelClinicInfo[] = [];


  await Promise.allSettled(SpecialtyClinicInfoPromises).then((promises) => {
    promises.forEach((promise) => {
      if (promise.status === "fulfilled") { //TODO: create crashes in parsers and ensure error propagates, salvage whatever data we could obtain before crash
        let index = organizedExcelClinicInfoArr.findIndex(
          (item) => item.clinicName === promise.value.clinicName
        );
        if (index === -1) {
          organizedExcelClinicInfoArr.push(promise.value);
        } else {
          if (promise.value.datesAndChairs !== undefined) {
            organizedExcelClinicInfoArr[index].datesAndChairs = [
              ...organizedExcelClinicInfoArr[index].datesAndChairs,
              ...promise.value.datesAndChairs,
            ];
          }
          if (promise.value.datesAndState !== undefined) {
            organizedExcelClinicInfoArr[index].datesAndState = [
              ...organizedExcelClinicInfoArr[index].datesAndState,
              ...promise.value.datesAndState,
            ];
          }
        }
      } else if (promise.status === "rejected") {
        const res = promise.reason as ExcelClinicInfo
        let index = organizedExcelClinicInfoArr.findIndex(
          (item) => item.clinicName === res.clinicName
        );
        if (index === -1) {
          organizedExcelClinicInfoArr.push(res);
        }
      }
    });
  });
  return organizedExcelClinicInfoArr;
}
